import type { RequestHandler } from 'msw';
import { companyFactoryHandlers } from '../company-factory/+mock/company-factory.handlers';
import { factoryDocumentHandlers } from '../document/+mock/factory-document.handlers';
import { factoryHandlers } from '../factory/+mock/factory.handlers';
import { factoryImageHandlers } from '../image/+mock/factory-image.handlers';
import {
  machineParkCombinedHandlers,
  machineParkDatasheetHandlers,
} from '../machine-park/+mock/machine-park.handlers';
import { factoryProjectReferenceHandlers } from '../project-reference/+mock/factory-project-reference.handlers';
import { virtualTourHandlers } from '../virtual-tour/+mock/virtual-tour.handlers';
import { vrCaptureRequestHandlers } from '../vr-capture-request/+mock/vr-capture-request.handlers';
import { createDemoFactoryMockState } from './demo-factory-stores';

export {
  createDemoFactoryMockState,
  DEMO_SUPPLIER_COMPANY_IDS,
  factoryEditToCompanySummary,
  type DemoFactoryMockState,
} from './demo-factory-stores';

export { companyFactoryHandlers } from '../company-factory/+mock/company-factory.handlers';
export { factoryDocumentHandlers } from '../document/+mock/factory-document.handlers';
export { factoryHandlers } from '../factory/+mock/factory.handlers';
export { factoryImageHandlers } from '../image/+mock/factory-image.handlers';
export {
  machineParkCombinedHandlers,
  machineParkDatasheetHandlers,
  machineParkHandlers,
} from '../machine-park/+mock/machine-park.handlers';
export { factoryProjectReferenceHandlers } from '../project-reference/+mock/factory-project-reference.handlers';
export { virtualTourHandlers } from '../virtual-tour/+mock/virtual-tour.handlers';
export { vrCaptureRequestHandlers } from '../vr-capture-request/+mock/vr-capture-request.handlers';
export {
  pendingVrCaptureFollowUps,
  resetVrCaptureRequestMockState,
  vrCaptureRequestStore,
} from '../vr-capture-request/+mock/vr-capture-request.mock-state';

/**
 * Aggregate of every MSW handler exposed by the factory data lib.
 *
 * Pass the API base URL the consuming app uses
 * (`EnvironmentService.getApiUrl()`) — the function returns the full
 * handler list so callers can spread it into `setupServer(...)` or
 * `provideMockBackend({ handlers: [...factoryDomainHandlers(api)] })`.
 *
 * Order matters when handlers overlap (MSW resolves first-match-wins);
 * the `:factoryId/...` sub-resources are listed before the generic
 * `/factories` collection so the parametric routes get a chance to
 * match before MSW falls back to the universal CRUD handlers.
 */
export function factoryDomainHandlers(baseUrl: string): RequestHandler[] {
  const demoState = createDemoFactoryMockState();
  return [
    ...factoryDocumentHandlers(baseUrl),
    ...factoryImageHandlers(baseUrl),
    ...machineParkCombinedHandlers(baseUrl),
    ...factoryProjectReferenceHandlers(baseUrl),
    ...virtualTourHandlers(baseUrl),
    ...vrCaptureRequestHandlers(baseUrl),
    ...companyFactoryHandlers(baseUrl, demoState),
    ...factoryHandlers(baseUrl, demoState),
  ];
}

/**
 * Narrow MSW handler subset for surfaces with no legacy backend equivalent:
 * gallery images, machine-park datasheets, and VR capture requests.
 *
 * Register via {@link provideMockBackendInterceptor} in local development
 * (HttpClient interceptor — local `apiUrl` is cross-origin staging, so the
 * browser MSW worker cannot intercept those requests). The demo build
 * continues to use {@link factoryDomainHandlers} with the browser worker.
 */
export function factoryLegacyGapHandlers(baseUrl: string): RequestHandler[] {
  return [
    ...factoryImageHandlers(baseUrl),
    ...machineParkDatasheetHandlers(baseUrl),
    ...vrCaptureRequestHandlers(baseUrl),
  ];
}
